import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * PROGRESS
 *
 * A track with a filled portion. The app had five of these built by hand and
 * they had already drifted: two heights (h-1, h-1.5), three track colours
 * (bg-muted, bg-border, bg-primary/20) and two transition durations, for what
 * is one idea.
 *
 * The fill is always `primary`. A progress bar that changes colour by value is
 * a different component — this one says "how far", not "how well".
 */
export const progressTrackRecipe = cva(
  [uiMarker, 'w-full overflow-hidden rounded-full bg-muted'],
  {
    variants: {
      size: {
        /** Hairline, for under a thumbnail or a card. */
        sm: 'h-1',
        /** The default. Visible without drawing attention. */
        md: 'h-1.5',
        /** When the number is the point of the screen. */
        lg: 'h-2.5',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

/**
 * The filled portion.
 *
 * Width comes from an inline style rather than a class: the value is a runtime
 * percentage, and Tailwind only generates classes it can see written out, so
 * `w-[${pct}%]` would compile, render, and produce no CSS at all.
 */
export const progressFillRecipe = cva([
  uiMarker,
  'h-full rounded-full bg-primary transition-[width] duration-slow ease-out',
]);

export type ProgressTrackRecipeProps = VariantProps<typeof progressTrackRecipe>;

/**
 * USAGE METER — how much of a plan's limit is used: "3 of 5 drivers".
 *
 * The different component the note above means. It shares the track, but its
 * fill says how close to the limit the count is: the usual colour, warning
 * once the app says it is near, destructive at the limit, where the next one
 * will be refused.
 */
export const usageMeterFillRecipe = cva(
  [uiMarker, 'h-full rounded-full transition-[width] duration-slow ease-out'],
  {
    variants: {
      tone: {
        default: 'bg-primary',
        warning: 'bg-warning',
        destructive: 'bg-destructive',
      },
    },
    defaultVariants: { tone: 'default' },
  }
);
